import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';

import MenuItem from 'flow/components/ui-components/MenuItem';
import useAppContext from 'flow/hooks/useAppContext';
import {
    selectAwaitingActivity,
    selectShowSetAwaitingActivityItem,
} from 'flow/selectors/TicketsSelectors';
import { selectOwnUserHasTicketPermission } from 'flow/selectors/ticket-permission-selectors';
import { updateAwaitingActivity } from 'flow/actions/tickets-action';
import { TICKET_STRINGS } from 'flow/utils/segment-utils';
import { segmentTrack } from 'flow/actions/segment-actions';
import { TicketKey } from 'flow/types/EntityKeys';
import useTicketAction from 'flow/hooks/useTicketAction';

type Props = {
    ticketKey: TicketKey;
};

const MenuItemNeedsAttention = ({ ticketKey }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const runTicketAction = useTicketAction();

    const canSetAwaitingActivity = useAppSelector((state) =>
        selectOwnUserHasTicketPermission(
            state,
            ticketKey,
            'setAwaitingActivity',
        ),
    );
    const isAwaitingActivity = useAppSelector((state) =>
        selectAwaitingActivity(state, ticketKey),
    );
    const showSetAwaitingActivityItem = useAppSelector((state) =>
        selectShowSetAwaitingActivityItem(state, ticketKey),
    );

    const [isSettingData, setIsSettingData] = useState(false);

    const setNeedsAttention = (value: boolean) => {
        setIsSettingData(true);
        runTicketAction(updateAwaitingActivity(ticketKey, value)).finally(
            () => {
                setIsSettingData(false);
            },
        );
        dispatch(
            segmentTrack(TICKET_STRINGS.NEEDS_ATTENTION, {
                component: 'ticket context menu',
                value: value,
            }),
        );
    };

    const onClickNeedsAttentionMenuItem = (event: React.MouseEvent) => {
        event?.stopPropagation();
        setNeedsAttention(!isAwaitingActivity);
    };

    if (!showSetAwaitingActivityItem) {
        return null;
    }

    return (
        <MenuItem
            onClick={onClickNeedsAttentionMenuItem}
            disabled={!canSetAwaitingActivity || isSettingData}
            tooltipLabel={
                canSetAwaitingActivity
                    ? undefined
                    : i18next.t('tickets.no_permission')
            }
        >
            <span>
                {isAwaitingActivity
                    ? i18next.t('Queue.History.Unmark')
                    : i18next.t('Queue.History.Mark')}
            </span>
        </MenuItem>
    );
};

export default MenuItemNeedsAttention;
